import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { Expand, Loader2, Minimize2, Move, Replace, Wand2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { setCursor } from "@/lib/cursor";
import { autoAlign } from "@/lib/autoAlign";
import { TrackFinder } from "@/components/telemetry/TrackFinder";
import { SatelliteMap } from "@/components/telemetry/SatelliteMap";
import { AlignControls } from "@/components/telemetry/AlignControls";
import { C_COLOR, TraceSvg } from "@/components/telemetry/TelemetryCharts";
import { useT } from "@/lib/i18n";
import { api, formatApiError } from "@/lib/api";
import { useTracks } from "@/lib/useTracks";
import { axisEnd, speedColor } from "@/lib/telemetry";
import { DEFAULT_ALIGN, gColor, hasAbsGps, hasGeo, projectTrace } from "@/lib/geo";

const chip = (active) => `px-2 py-0.5 font-mono text-[10px] uppercase border rounded-sm transition-colors duration-200 ${active ? "border-[#FFB800] text-[#FFB800] bg-[#FFB800]/10" : "border-[#282D3D] text-slate-400 hover:text-white"}`;

function useColoring(lap, channels, t) {
  const find = (rx) => channels.find((c) => rx.test(c));
  const modes = [["speed", find(/speed/i), t("color_speed")], ["glat", find(/^g[ _]?lat/i), t("color_glat")], ["glon", find(/^g[ _]?lon/i), t("color_glon")]].filter((m) => m[1]);
  const [mode, setMode] = useState(modes[0]?.[0]);
  const cur = modes.find((m) => m[0] === mode);
  const result = useMemo(() => {
    const v = cur ? lap.ch[cur[1]] : null;
    if (!v) return { colors: lap.x.map(() => "#94A3B8"), min: 0, max: 0 };
    const min = Math.min(...v), max = Math.max(...v), absMax = Math.max(Math.abs(min), Math.abs(max));
    const colors = v.map((x) => (mode === "speed" ? speedColor((x - min) / (max - min || 1)) : gColor(x, absMax)));
    return { colors, min, max };
  }, [lap, cur, mode]);
  return { modes, mode, setMode, ...result };
}

function useTips(lap, channels) {
  return useMemo(() => {
    const shown = channels.filter((c) => /speed|^g[ _]?(lat|lon)|rpm/i.test(c));
    return lap.d.map((d, i) => `${Math.round(d)} m · ${lap.t[i]?.toFixed(2)} s · ${shown.map((c) => `${c} ${lap.ch[c]?.[i] ?? "—"}`).join(" · ")}`);
  }, [lap, channels]);
}

function useAutoAlign(lap, track, setAlign, setAligning, reload, t) {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async (silent = false) => {
    setBusy(true);
    try {
      await new Promise((r) => setTimeout(r, 30));
      const res = await autoAlign(lap, track);
      if (!res) {
        if (!silent) toast.error(t("auto_align_none"));
        return;
      }
      setAlign(res.align);
      const err = ` (±${res.err.toFixed(1)} m)`;
      if (res.err < 4) {
        await api.put(`/tracks/${track.id}/align`, res.align);
        toast.success(t("auto_align_saved") + err);
        reload();
      } else {
        setAligning(true);
        toast.warning(t("auto_align_check") + err);
      }
    } catch {
      if (!silent) toast.error(t("auto_align_failed"));
    } finally {
      setBusy(false);
    }
  }, [lap, track, setAlign, setAligning, reload, t]);
  return { busy, run };
}

const Legend = ({ min, max, mode }) => (
  <div data-testid="map-legend" className="flex items-center gap-2 font-mono text-[10px] text-slate-400 mt-2">
    <span>{min.toFixed(mode === "speed" ? 0 : 2)}</span>
    <span className="flex-1 h-1.5 rounded-sm" style={{ background: mode === "speed" ? "linear-gradient(90deg,hsl(220,90%,55%),hsl(110,90%,55%),hsl(0,90%,55%))" : "linear-gradient(90deg,hsl(210,95%,50%),hsl(0,0%,92%),hsl(5,95%,50%))" }} />
    <span>{max.toFixed(mode === "speed" ? 0 : 2)}</span>
  </div>
);

const Scrubber = ({ lap, axis }) => {
  const [v, setV] = useState(0);
  const end = axisEnd(lap, axis);
  const move = ([x]) => {
    setV(x);
    setCursor(x);
  };
  return (
    <div data-testid="map-scrubber" className="flex items-center gap-3 mt-3">
      <Slider min={0} max={end} step={end / 500} value={[Math.min(v, end)]} onValueChange={move} className="flex-1" />
      <span className="font-mono text-xs text-[#FFB800] w-20 text-right">{axis === "t" ? `${v.toFixed(2)} s` : `${Math.round(v)} m`}</span>
    </div>
  );
};

export const MapPanel = ({ sessionId, trackId, setForm, lap, lapB, lapC, channels, csvTrack, mapKey, axis, marks, mapH = 460, fill = false }) => {
  const { t } = useT();
  const { tracks, reload } = useTracks();
  const track = tracks.find((x) => x.id === trackId);
  const [align, setAlign] = useState(DEFAULT_ALIGN);
  const [aligning, setAligning] = useState(false);
  const [picking, setPicking] = useState(false);
  const [finder, setFinder] = useState(false);
  const [full, setFull] = useState(false);
  const col = useColoring(lap, channels, t);
  const tips = useTips(lap, channels);
  useEffect(() => {
    setAlign({ ...DEFAULT_ALIGN, ...(track?.align || {}) });
  }, [track]);
  const abs = hasAbsGps(lap);
  const geo = abs || (hasGeo(track) && lap.x?.length > 0);
  const points = useMemo(() => (geo ? projectTrace(lap, track, align) : []), [geo, lap, track, align]);
  const canProject = (l) => l && (hasAbsGps(l) || (hasGeo(track) && l.x?.length > 0));
  const pointsB = useMemo(() => (geo && canProject(lapB) ? projectTrace(lapB, track, align) : null), [geo, lapB, track, align]); // eslint-disable-line react-hooks/exhaustive-deps
  const pointsC = useMemo(() => (geo && canProject(lapC) ? projectTrace(lapC, track, align) : null), [geo, lapC, track, align]); // eslint-disable-line react-hooks/exhaustive-deps
  const cursors = useMemo(() => [{ lap, points, color: "#007AFF" }, ...(pointsB ? [{ lap: lapB, points: pointsB, color: "#FF3B30" }] : []),
    ...(pointsC ? [{ lap: lapC, points: pointsC, color: C_COLOR }] : [])], [lap, points, lapB, pointsB, lapC, pointsC]);
  const auto = useAutoAlign(lap, track, setAlign, setAligning, reload, t);
  const autoRan = useRef(null);
  useEffect(() => {
    if (geo && !abs && !track.align && autoRan.current !== track.id) {
      autoRan.current = track.id;
      auto.run(true);
    }
  }, [geo, abs, track, auto]);
  const onPick = useCallback((ll) => {
    setAlign((a) => ({ ...a, lat: ll.lat, lon: ll.lng }));
    setPicking(false);
  }, []);

  const selectTrack = async (id) => {
    try {
      await api.put(`/sessions/${sessionId}/track`, { track_id: id });
      await reload();
      setForm((f) => ({ ...f, track_id: id }));
      setFinder(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const saveAlign = async () => {
    await api.put(`/tracks/${track.id}/align`, align);
    toast.success(t("alignment_saved"));
    reload();
  };

  const body = (
    <>
    {full && <div className="fixed inset-0 z-[60] bg-black/75" onClick={() => setFull(false)} />}
    <div data-testid="telemetry-track-map" className={`bg-[#12141C] border border-[#282D3D] rounded-sm p-3 ${full ? "fixed inset-3 sm:inset-6 z-[70] overflow-auto" : ""}`}>
      <div className="flex items-center gap-2 mb-2 min-h-8">
        <span className="font-mono text-[10px] uppercase tracking-widest text-slate-400 shrink-0">{t("track_map")}</span>
        {track ? (
          <>
            <span data-testid="telemetry-track-name" className="min-w-0 truncate font-heading uppercase font-bold text-base">{track.name}</span>
            {sessionId && (
              <button type="button" data-testid="map-change-track-btn" onClick={() => setFinder(!finder)} className={`ml-auto shrink-0 flex items-center gap-1 ${chip(finder)}`}>
                <Replace className="w-3 h-3" /> {t("change_track")}
              </button>
            )}
          </>
        ) : (
          <span data-testid="map-track-not-detected" className="font-mono text-[10px] text-[#FFB800]">{t("track_not_detected")}</span>
        )}
        <button type="button" data-testid="map-fullscreen-btn" onClick={() => setFull(!full)} className={`${track && sessionId ? "" : "ml-auto"} shrink-0 flex items-center gap-1 ${chip(full)}`}>
          {full ? <Minimize2 className="w-3 h-3" /> : <Expand className="w-3 h-3" />} {full ? t("exit_fullscreen") : t("fullscreen")}
        </button>
      </div>
      {sessionId && (finder || !track) && (
        <TrackFinder tracks={tracks} trackId={trackId} csvTrack={csvTrack} onSelect={selectTrack} onClose={track ? () => setFinder(false) : null} />
      )}
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {col.modes.map(([k, , label]) => <button type="button" key={k} data-testid={`map-color-${k}`} onClick={() => col.setMode(k)} className={chip(col.mode === k)}>{label}</button>)}
        {geo && !abs && (
          <span className="ml-auto flex gap-1.5">
            <button type="button" data-testid="map-auto-align-btn" disabled={auto.busy} onClick={() => auto.run()} className={`flex items-center gap-1 ${chip(false)}`}>
              {auto.busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} {auto.busy ? t("auto_aligning") : t("auto_align")}
            </button>
            <button type="button" data-testid="map-align-toggle" onClick={() => setAligning(!aligning)} className={`flex items-center gap-1 ${chip(aligning)}`}><Move className="w-3 h-3" /> {t("align")}</button>
          </span>
        )}
        {abs && <span data-testid="map-gps-badge" className="ml-auto font-mono text-[10px] uppercase text-[#10B981]">{t("gps_absolute")}</span>}
      </div>
      {geo ? (
        <SatelliteMap key={abs ? `gps-${mapKey}` : track.id} center={abs ? [lap.lat[0], lap.lon[0]] : [track.lat, track.lon]} points={points} colors={col.colors} tips={tips}
          cursors={cursors} axis={axis} marks={marks} picking={picking} onPick={onPick} h={full ? Math.max(320, window.innerHeight - 200) : mapH} />
      ) : (
        <div style={{ height: full ? Math.max(320, window.innerHeight - 230) : fill ? mapH : Math.min(mapH, 420) }}><TraceSvg lap={lap} lapB={lapB} lapC={lapC} colors={col.colors} axis={axis} /></div>
      )}
      <Legend min={col.min} max={col.max} mode={col.mode} />
      {full && <Scrubber lap={lap} axis={axis} />}
      {geo && !abs && aligning && <AlignControls align={align} setAlign={setAlign} track={track} picking={picking} setPicking={setPicking} onSave={saveAlign} />}
    </div>
    </>
  );
  return full ? createPortal(body, document.body) : body;
};
